/**
 * Auto-Smooth Shading Algorithm
 * 
 * Apply smooth shading sambil preserve sharp edges
 * Edges sharper than threshold angle tetap hard
 */

/**
 * Apply auto-smooth normals ke mesh geometry
 * @param {THREE.BufferGeometry} geometry - Mesh geometry
 * @param {number} angleThresholdDegrees - Angle threshold dalam degrees (default: 30)
 * @param {Object} THREE - Three.js module
 * @returns {boolean} Success status
 */
export function applyAutoSmoothNormals(geometry, angleThresholdDegrees, THREE) {
  console.log('🔧 Applying auto smooth...')
  
  try {
    const position = geometry.attributes.position
    const index = geometry.index

    if (!position || !index) {
      console.log('   ⚠️ No index buffer, using standard smooth')
      geometry.computeVertexNormals()
      return false
    }

    // Convert angle from degrees to radians
    const angleThreshold = (angleThresholdDegrees * Math.PI) / 180
    console.log(
      `   Angle threshold: ${angleThresholdDegrees}° (${angleThreshold.toFixed(3)} rad)`
    )

    // Create new normals array
    const normals = new Float32Array(position.count * 3)

    // Build vertex-to-faces mapping
    const vertexFaces = []
    for (let i = 0; i < position.count; i++) {
      vertexFaces[i] = []
    }

    // Store face data (normal and vertices)
    const faces = []
    const vA = new THREE.Vector3()
    const vB = new THREE.Vector3()
    const vC = new THREE.Vector3()
    const cb = new THREE.Vector3()
    const ab = new THREE.Vector3()

    // Calculate face normals
    for (let i = 0; i < index.count; i += 3) {
      const a = index.getX(i)
      const b = index.getX(i + 1)
      const c = index.getX(i + 2)

      vA.fromBufferAttribute(position, a)
      vB.fromBufferAttribute(position, b)
      vC.fromBufferAttribute(position, c)

      cb.subVectors(vC, vB)
      ab.subVectors(vA, vB)
      cb.cross(ab)
      cb.normalize()

      const faceIndex = faces.length
      faces.push({
        normal: cb.clone(),
        vertices: [a, b, c],
      })

      vertexFaces[a].push(faceIndex)
      vertexFaces[b].push(faceIndex)
      vertexFaces[c].push(faceIndex)
    }

    // Compute vertex normals with auto smooth
    for (let i = 0; i < position.count; i++) {
      const adjacentFaces = vertexFaces[i]
      if (adjacentFaces.length === 0) continue

      const smoothedNormal = new THREE.Vector3()

      // For each face connected to this vertex
      for (const faceIdx of adjacentFaces) {
        const face = faces[faceIdx]
        const faceNormal = face.normal

        // Check if this face should be smoothed with others
        let smooth = true
        for (const otherFaceIdx of adjacentFaces) {
          if (faceIdx === otherFaceIdx) continue

          const otherFace = faces[otherFaceIdx]
          const angle = faceNormal.angleTo(otherFace.normal)

          // If angle is too large, don't smooth with this face
          if (angle > angleThreshold) {
            smooth = false
            break
          }
        }

        // Only add normals that pass the angle test
        if (smooth) {
          smoothedNormal.add(faceNormal)
        }
      }

      // If no faces passed, use the average of all adjacent faces
      if (smoothedNormal.lengthSq() === 0) {
        for (const faceIdx of adjacentFaces) {
          smoothedNormal.add(faces[faceIdx].normal)
        }
      }

      smoothedNormal.normalize()
      normals[i * 3] = smoothedNormal.x
      normals[i * 3 + 1] = smoothedNormal.y
      normals[i * 3 + 2] = smoothedNormal.z
    }

    // Apply new normals
    geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3))
    geometry.attributes.normal.needsUpdate = true

    console.log('   ✓ Auto smooth applied successfully')
    return true
  } catch (error) {
    console.warn('⚠️ Auto smooth failed:', error)
    // Fallback to regular smooth shading
    geometry.computeVertexNormals()
    return false
  }
}

